import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const videoUrl = "https://player.vimeo.com/video/1107753546?h=4792721dbe&controls=0&badge=0&title=0&byline=0&portrait=0&autoplay=0&dnt=1";

function formatTime(seconds) {
	const wholeSeconds = Math.floor(Number.isFinite(seconds) ? seconds : 0);
	return `${Math.floor(wholeSeconds / 60)}:${String(wholeSeconds % 60).padStart(2, "0")}`;
}

function VideoModal({ onClose }) {
	const dialogRef = useRef(null);
	const closeButtonRef = useRef(null);
	const iframeRef = useRef(null);
	const playerRef = useRef(null);
	const [isReady, setIsReady] = useState(false);
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [error, setError] = useState("");

	useEffect(() => {
		const previouslyFocused = document.activeElement;
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		closeButtonRef.current?.focus();

		function handleKeyDown(event) {
			if (event.key === "Escape") {
				onClose();
				return;
			}
			if (event.key !== "Tab") return;
			const focusable = Array.from(dialogRef.current?.querySelectorAll("button:not(:disabled), input:not(:disabled)") || []);
			if (!focusable.length) return;
			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		}

		document.addEventListener("keydown", handleKeyDown);
		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			document.body.style.overflow = previousOverflow;
			previouslyFocused?.focus?.();
		};
	}, [onClose]);

	useEffect(() => {
		let active = true;
		let player;
		const onPlay = () => setIsPlaying(true);
		const onPause = () => setIsPlaying(false);
		const onTimeUpdate = ({ seconds, duration: videoDuration }) => {
			setCurrentTime(seconds);
			setDuration(videoDuration);
		};

		import("@vimeo/player")
			.then(({ default: VimeoPlayer }) => {
				if (!active || !iframeRef.current) return;
				player = new VimeoPlayer(iframeRef.current);
				playerRef.current = player;
				player.on("play", onPlay);
				player.on("pause", onPause);
				player.on("ended", onPause);
				player.on("timeupdate", onTimeUpdate);
				return player.ready().then(() => player.getDuration());
			})
			.then((videoDuration) => {
				if (!active || videoDuration === undefined) return;
				setDuration(videoDuration);
				setIsReady(true);
			})
			.catch(() => {
				if (active) setError("The video could not be loaded. Please try again.");
			});

		return () => {
			active = false;
			if (player) {
				player.off("play", onPlay);
				player.off("pause", onPause);
				player.off("ended", onPause);
				player.off("timeupdate", onTimeUpdate);
				player.pause().catch(() => {});
			}
			playerRef.current = null;
		};
	}, []);

	function togglePlayback() {
		if (!playerRef.current) return;
		const action = isPlaying ? playerRef.current.pause() : playerRef.current.play();
		action.catch(() => setError("Playback could not start. Please try again."));
	}

	function seek(event) {
		if (!playerRef.current) return;
		const seconds = Number(event.target.value);
		setCurrentTime(seconds);
		playerRef.current.setCurrentTime(seconds).catch(() => setError("The video could not seek. Please try again."));
	}

	return createPortal(
		<div className="video-modal__backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
			<section ref={dialogRef} className="video-modal" role="dialog" aria-modal="true" aria-label="ChampionBred video">
				<button ref={closeButtonRef} type="button" className="video-modal__close" onClick={onClose} aria-label="Close video">
					<i className="fa fa-times" aria-hidden="true" />
				</button>
				<div className="video-modal__frame">
					<iframe ref={iframeRef} src={videoUrl} title="ChampionBred video" allow="autoplay" tabIndex={-1} />
					{!isReady && !error ? <span className="video-modal__loading" role="status">Loading video…</span> : null}
				</div>
				<div className="video-modal__controls">
					<button type="button" onClick={togglePlayback} disabled={!isReady} aria-label={isPlaying ? "Pause video" : "Play video"}>
						<i className={`fa fa-${isPlaying ? "pause" : "play"}`} aria-hidden="true" />
					</button>
					<input type="range" min="0" max={duration || 1} step="0.1" value={Math.min(currentTime, duration || 1)} onChange={seek} disabled={!isReady || !duration} aria-label="Video playback position" aria-valuetext={`${formatTime(currentTime)} of ${formatTime(duration)}`} style={{ "--video-progress": `${duration ? (currentTime / duration) * 100 : 0}%` }} />
					<span className="video-modal__time" aria-hidden="true">{formatTime(currentTime)} / {formatTime(duration)}</span>
				</div>
				{error ? <p className="video-modal__error" role="alert">{error}</p> : null}
			</section>
		</div>,
		document.body
	);
}

export default VideoModal;
